import { useCallback } from 'react';

import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gql';

import { usePerimeterXTracking } from '#src/app/shared/hooks/payments';
import { usePerimeterXCaptcha } from '#src/app/shared/hooks/perimeterX';

// Identifies this flow in the px_captcha_* analytics, matching the convention used by the other
// protected flows (corgi_promote, corgi_sign, corgi_ambassadors_contract, ...).
const FORM = 'homepage_social_post';

/**
 * Both social post routes are in the router's px_enforced_routes, so PerimeterX can answer either
 * request with a challenge instead of data. Runs `request`, and on a challenge opens the captcha and
 * retries once if it's solved. An unsolved (or abandoned) challenge rethrows the original error so
 * callers fall through to their normal error handling.
 */
export function useWithPerimeterXCaptcha(): <T>(request: () => Promise<T>) => Promise<T> {
	const { trackShow, trackSuccess, trackFailure } = usePerimeterXTracking(FORM);
	const triggerCaptcha = usePerimeterXCaptcha();

	return useCallback(
		async <T>(request: () => Promise<T>): Promise<T> => {
			try {
				return await request();
			} catch (error) {
				if (!(error instanceof GQLResponsePerimeterXChallengeError)) throw error;

				trackShow(error.operationName);
				const solved = await triggerCaptcha({ pxResponse: error.pxChallenge });
				if (!solved) {
					trackFailure(error.pxChallenge, error.operationName);
					throw error;
				}
				trackSuccess(error.pxChallenge, error.operationName);

				// Only one retry, so a challenge that keeps coming back can't trap the user in a loop.
				return request();
			}
		},
		[triggerCaptcha, trackShow, trackSuccess, trackFailure],
	);
}
